import { FormInstance, Space, Typography } from 'antd';
import { isEmpty } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import {
  JiraApi,
  JiraConfig,
  ProjectMappingMechanism,
  ProjectValidationResponse,
  SettingsMeta,
  SourceType,
} from 'api/integrations/jira';
import { LinkButton } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Radio } from 'components/atoms/radio';
import { useTranslation } from 'hooks/use-translation';
import {
  GET_JIRA_CONFIG,
  GET_JIRA_PROJECT_EPIC,
  useQueryGetJiraProjects,
} from 'queries/jira-integration';
import { Title } from '../styles';
import { SectionKeys } from '../types';
import { ProjectChangeModel } from './project-change-modal';
import { ProjectChangeWarning } from './project-change-warning';

interface ProjectSelection {
  jiraConfig: JiraConfig;
  onChange: (updatedObject: SettingsMeta, key?: SectionKeys) => void;
  updating?: SectionKeys;
}

const { Text } = Typography;

export const ProjectSelection: FC<ProjectSelection> = ({
  jiraConfig,
  onChange,
  updating,
}) => {
  const queryClient = useQueryClient();

  const [projectSelectForm] = Form.useForm<{ fixedProjectId: number }>();
  const [projectMappingForm] = Form.useForm<JiraConfig>();
  const [projectModalVisibility, setProjectModalVisibility] = useState(false);
  const [erroredTemplates, setErroredTemplates] =
    useState<ProjectValidationResponse[]>();

  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const { data: jiraProjects, isLoading: loadingProjects } =
    useQueryGetJiraProjects(jiraConfig.integrationId);

  const title = useTranslation(
    'settings.integrations.jira.choose_project.title',
  );

  const selectProjectText = useTranslation(
    'settings.integrations.jira.choose_project.help',
  );

  const fixedProjectText = useTranslation(
    'settings.integrations.jira.choose_project.fixed',
  );

  const promptProjectText = useTranslation(
    'settings.integrations.jira.choose_project.prompt',
  );

  const queryClientRef = useRef(queryClient);

  const updateJiraProject = useCallback(() => {
    const payload = projectMappingForm.getFieldsValue(true);

    onChange(
      {
        ...jiraConfig.settingsMeta,
        ...payload,
      },
      SectionKeys.PROJECT_SELECTION,
    );

    queryClientRef?.current.invalidateQueries(GET_JIRA_CONFIG);
    queryClientRef?.current.invalidateQueries([
      GET_JIRA_PROJECT_EPIC,
      jiraConfig.integrationId,
      jiraConfig.settingsMeta.fixedProjectId,
    ]);
    setErroredTemplates(undefined);
  }, [projectMappingForm, queryClientRef, jiraConfig, onChange]);

  const selectedProject = useMemo(() => {
    return jiraProjects?.find(
      (jiraProject) =>
        jiraProject.id === jiraConfig.settingsMeta.fixedProjectId,
    );
  }, [jiraProjects, jiraConfig.settingsMeta.fixedProjectId]);

  useEffect(() => {
    if (updating) {
      if (updating === SectionKeys.PROJECT_SELECTION) {
        setLoading(true);
      } else {
        setDisabled(true);
      }
    } else {
      setLoading(false);
      setDisabled(false);
    }
  }, [updating, setLoading, setDisabled]);

  const validateProjectChange = useCallback(() => {
    setProjectModalVisibility(false);
    const payload = projectMappingForm.getFieldsValue(true);

    JiraApi.validJiraProjectChange(jiraConfig.integrationId, payload)
      .then(() => {
        updateJiraProject();
      })
      .catch(
        ({
          response: {
            data: { errors },
            status,
          },
        }: {
          response: { data: { errors: ProjectValidationResponse[] }; status };
        }) => {
          if (status === 400) {
            setErroredTemplates(
              errors?.filter(
                (error) => error.resourceType === SourceType.Templates,
              ),
            );
          }
        },
      );
  }, [jiraConfig.integrationId, projectMappingForm, updateJiraProject]);

  const onProjectSelectionProviderChange = useCallback(
    async (
      name: string,
      { forms }: { forms: Record<string, FormInstance> },
    ) => {
      if (name === 'projectMappingForm') {
        const projectMappingFormValues =
          forms.projectMappingForm.getFieldsValue(true) as Record<
            string,
            string
          >;
        const isDynamicMapping =
          projectMappingFormValues.projectMappingMechanism ===
          ProjectMappingMechanism.PromptDuringIssueCreation;

        if (isDynamicMapping) {
          validateProjectChange();
        } else if (!isDynamicMapping) {
          setProjectModalVisibility(true);
        }
      }
    },
    [validateProjectChange],
  );

  const onProjectSelectionProviderSubmit = useCallback(
    async (
      name: string,
      { forms }: { forms: Record<string, FormInstance> },
    ) => {
      if (name === 'projectSelectionForm') {
        const {
          projectSelectionForm,
          projectMappingForm: _projectMappingForm,
        } = forms;

        await _projectMappingForm.setFieldsValue({
          fixedProjectId:
            projectSelectionForm?.getFieldValue('fixedProjectId')?.value,
        });
        validateProjectChange();
      }
    },
    [validateProjectChange],
  );

  const handleCancelUpdate = () => {
    setErroredTemplates(undefined);
    projectMappingForm.resetFields();
    projectSelectForm.resetFields();
  };

  return (
    <>
      <Title>{title}</Title>

      <Form.Provider
        onFormChange={onProjectSelectionProviderChange as any}
        onFormFinish={onProjectSelectionProviderSubmit as any}
      >
        <Form
          name="projectMappingForm"
          initialValues={jiraConfig.settingsMeta}
          form={projectMappingForm}
        >
          <Form.Item name="projectMappingMechanism">
            <Radio.Group disabled={loading}>
              <Space direction="vertical">
                <Radio
                  value={ProjectMappingMechanism.PromptDuringIssueCreation}
                >
                  {promptProjectText}
                </Radio>
                <Radio value={ProjectMappingMechanism.Fixed}>
                  <Space direction="vertical">
                    <Text>{fixedProjectText}</Text>
                    {jiraConfig.settingsMeta.projectMappingMechanism ===
                      ProjectMappingMechanism.Fixed && (
                      <LinkButton
                        loading={loading}
                        disabled={loading || disabled}
                        onClick={() => setProjectModalVisibility(true)}
                      >
                        {isEmpty(selectedProject)
                          ? selectProjectText
                          : selectedProject?.name}
                      </LinkButton>
                    )}
                  </Space>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="fixedProjectId" hidden>
            <Input />
          </Form.Item>
        </Form>

        {projectModalVisibility ? (
          <ProjectChangeModel
            jiraConfig={jiraConfig}
            projectSelectForm={projectSelectForm}
            jiraProjects={jiraProjects ?? []}
            onModalClose={setProjectModalVisibility}
            loading={loading || loadingProjects}
            selectedProject={selectedProject}
            validateProjectChange={validateProjectChange}
          />
        ) : null}
      </Form.Provider>

      <ProjectChangeWarning
        erroredTemplates={erroredTemplates}
        confirmUpdate={updateJiraProject}
        cancelUpdate={handleCancelUpdate}
      />
    </>
  );
};
